import React, { useState, useEffect } from 'react';
import { Plus, Video, X } from 'lucide-react';
import { Promotion, PromotionType } from '../../../shared/types';

const categories = ['Frutas', 'Verduras', 'Carnes', 'Laticínios', 'Bebidas', 'Padaria', 'Limpeza', 'Higiene', 'Promoção Especial'];

interface PromotionFormModalProps {
    editingPromo: Promotion | null;
    onClose: () => void;
    onSave: (promoData: any, id?: number) => void;
}

export const PromotionFormModal: React.FC<PromotionFormModalProps> = ({
    editingPromo,
    onClose,
    onSave
}) => {
    const [promotionType, setPromotionType] = useState<PromotionType>('product');
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        originalPrice: '',
        unit: '',
        image: '',
        video: '',
        hasAudio: true,
        autoplay: true,
        loop: true,
        isVideoPromotion: false,
        validUntil: '',
        description: '',
        category: ''
    });

    useEffect(() => {
        if (editingPromo) {
            setPromotionType(editingPromo.isVideoPromotion ? 'video' : 'product');
            setFormData({
                name: editingPromo.name,
                price: editingPromo.price.toString(),
                originalPrice: editingPromo.originalPrice?.toString() || '',
                unit: editingPromo.unit,
                image: editingPromo.image || '',
                video: editingPromo.video || '',
                hasAudio: editingPromo.hasAudio !== false,
                autoplay: editingPromo.autoplay !== false,
                loop: editingPromo.loop !== false,
                isVideoPromotion: editingPromo.isVideoPromotion || false,
                validUntil: editingPromo.validUntil,
                description: editingPromo.description || '',
                category: editingPromo.category
            });
        }
    }, [editingPromo]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const value = e.target.type === 'checkbox'
            ? (e.target as HTMLInputElement).checked
            : e.target.value;

        setFormData({
            ...formData,
            [e.target.name]: value
        });
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setFormData({
                    ...formData,
                    image: e.target?.result as string
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('video/')) {
                alert('Por favor, selecione um arquivo de vídeo válido');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                setFormData({
                    ...formData,
                    video: e.target?.result as string
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePromotionTypeChange = (type: PromotionType) => {
        setPromotionType(type);
        setFormData({
            ...formData,
            isVideoPromotion: type === 'video'
        });
    };

    const handleSubmit = () => {
        if (promotionType === 'product') {
            if (!formData.name || !formData.price || !formData.unit || !formData.validUntil || !formData.category) {
                alert('Por favor, preencha todos os campos obrigatórios');
                return;
            }
        } else {
            if (!formData.name || !formData.video || !formData.validUntil || !formData.category) {
                alert('Por favor, preencha todos os campos obrigatórios');
                return;
            }
        }

        const promoData = {
            ...formData,
            price: Number(formData.price) || 0,
            originalPrice: formData.originalPrice ? Number(formData.originalPrice) : undefined,
            isVideoPromotion: promotionType === 'video'
        };

        onSave(promoData, editingPromo?.id);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">
                            {editingPromo ? 'Editar Promoção' : 'Nova Promoção'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Promotion Type Selector */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tipo de Promoção
                        </label>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => handlePromotionTypeChange('product')}
                                className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 ${promotionType === 'product'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                    }`}
                            >
                                <Plus size={18} />
                                Produto
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePromotionTypeChange('video')}
                                className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 ${promotionType === 'video'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                    }`}
                            >
                                <Video size={18} />
                                Vídeo
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nome da Promoção *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                                placeholder={promotionType === 'product' ? "Ex: Banana Prata" : "Ex: Promoção Especial de Verão"}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Categoria *
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                            >
                                <option value="">Selecione uma categoria</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        {promotionType === 'product' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Preço Promocional *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Preço Original
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="originalPrice"
                                        value={formData.originalPrice}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Unidade *
                                    </label>
                                    <input
                                        type="text"
                                        name="unit"
                                        value={formData.unit}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg"
                                        placeholder="Ex: kg, unidade, litro"
                                    />
                                </div>
                            </>
                        )}

                        <div className={promotionType === 'product' ? '' : 'col-span-2'}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Válido até *
                            </label>
                            <input
                                type="date"
                                name="validUntil"
                                value={formData.validUntil}
                                onChange={handleInputChange}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Descrição
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={3}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                                placeholder="Descrição opcional..."
                            />
                        </div>

                        {promotionType === 'product' ? (
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Imagem do Produto
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                />
                                {formData.image && (
                                    <div className="mt-4">
                                        <img
                                            src={formData.image}
                                            alt="Preview"
                                            className="w-full max-h-48 object-contain rounded-lg border"
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Vídeo Promocional *
                                    </label>
                                    <input
                                        type="file"
                                        accept="video/*"
                                        onChange={handleVideoUpload}
                                        className="w-full p-3 border border-gray-300 rounded-lg"
                                    />
                                    {formData.video && (
                                        <div className="mt-4">
                                            <video
                                                src={formData.video}
                                                controls
                                                className="w-full max-h-48 rounded-lg border"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="col-span-2 space-y-3">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="hasAudio"
                                            checked={formData.hasAudio}
                                            onChange={handleInputChange}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm text-gray-700">Com áudio</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="autoplay"
                                            checked={formData.autoplay}
                                            onChange={handleInputChange}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm text-gray-700">Reprodução automática</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="loop"
                                            checked={formData.loop}
                                            onChange={handleInputChange}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm text-gray-700">Repetir em loop</span>
                                    </label>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button
                            onClick={handleSubmit}
                            className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            {editingPromo ? 'Salvar Alterações' : 'Criar Promoção'}
                        </button>
                        <button
                            onClick={onClose}
                            className="px-6 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
